import React, { useEffect, useMemo, useState, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import '../AdminDashboard/BalanceEnquiry.css';
import './SecondaryBalanceEnquiry.css';

const SecondaryBalanceEnquiry = ({
  dateFrom: controlledDateFrom,
  dateTo: controlledDateTo,
  hideFilters = false,
  title = 'Reference Numbers (System Credits / Debits)',
}) => {
  const [dateFrom, setDateFrom] = useState(controlledDateFrom || '');
  const [dateTo, setDateTo] = useState(controlledDateTo || '');
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  useEffect(() => {
    if (controlledDateFrom && controlledDateTo) {
      fetchBalanceSummary(controlledDateFrom, controlledDateTo);
    } else {
      fetchBalanceSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledDateFrom, controlledDateTo]);

  const activeDateFrom = controlledDateFrom || dateFrom;
  const activeDateTo = controlledDateTo || dateTo;

  const fetchBalanceSummary = async (from = activeDateFrom, to = activeDateTo) => {
    setLoading(true);
    setError('');

    try {
      let url = `${API_BASE_URL}/api/secondary-balance-enquiry`;

      if (from && to) {
        url += `?date_from=${from}&date_to=${to}`;
      }

      const requests = [fetch(url)];
      if (from && to) {
        requests.push(fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${to}`));
        if (from !== to) {
          requests.push(fetch(`${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${from}`));
        }
      }

      const responses = await Promise.all(requests);
      const balanceResponse = responses[0];
      if (!balanceResponse.ok) {
        const payload = await balanceResponse.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch balance data');
      }

      const data = await balanceResponse.json();

      if (from && to && responses[1]?.ok) {
        const closingDaily = await responses[1].json();
        let openingBalance = closingDaily.secondaryOpeningBalance;

        if (from !== to && responses[2]?.ok) {
          const openingDaily = await responses[2].json();
          openingBalance = openingDaily.secondaryOpeningBalance;
        }

        data.secondaryOpeningBalance = openingBalance;
        data.secondaryClosingBalance = closingDaily.secondaryClosingBalance;
        data.secondaryDayNetBalance = from === to
          ? closingDaily.secondaryDayNet
          : closingDaily.secondaryClosingBalance - openingBalance;
      }

      setBalanceData(data);
    } catch (err) {
      console.error('Failed to fetch secondary balance enquiry data:', err);
      setError(err.message || 'Unexpected error occurred while loading balance data.');
      setBalanceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      alert('From date cannot be greater than To date.');
      return;
    }
    fetchBalanceSummary();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchBalanceSummary('', '');
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return `₹ ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const secondaryRows = useMemo(() => {
    if (!balanceData) {
      return { debits: [], credits: [] };
    }

    // Debits (Red)
    const debits = [
      { label: 'General Loan (Debit)', value: balanceData.secondaryGeneralLoanDebit, isProfit: false },
      { label: 'CD Loan (Debit)', value: balanceData.secondaryCdLoanDebit, isProfit: false },
      { label: 'TBD Loan (Debit)', value: balanceData.secondaryTbdLoanDebit, isProfit: false },
      { label: 'Expense (Debit)', value: balanceData.secondaryExpenseDebit, isProfit: false },
      { label: 'Partner Meeting (Debit)', value: balanceData.secondaryPartnerMeetingDebit, isProfit: false },
      { label: 'RD Lucky Draw (Debit)', value: balanceData.secondaryRdDebit, isProfit: false },
      { label: 'Excel Transaction (Debit)', value: balanceData.secondaryExcelDebits, isProfit: false },
      { label: 'Partner Transaction (Debit)', value: balanceData.secondaryPartnerDebits, isProfit: false },
    ];

    // Credits (Green) - with PROFIT labels where applicable
    const credits = [
      { label: 'GeneralLoan Interest (Credit)', value: balanceData.secondaryGeneralLoanInterest, isProfit: true },
      { label: 'CD Loan Interest (Credit)', value: balanceData.secondaryCdLoanInterest, isProfit: true },
      { label: 'TBD Loan Interest (Credit)', value: balanceData.secondaryTbdLoanInterest, isProfit: true },
      { label: 'GeneralLoan EMI (Credit)', value: balanceData.secondaryGeneralLoanEmi, isProfit: false },
      { label: 'CD Loan EMI (Credit)', value: balanceData.secondaryCdLoanEmi, isProfit: false },
      { label: 'TBD Loan EMI (Credit)', value: balanceData.secondaryTbdLoanEmi, isProfit: false },
      { label: 'GL Overdue (Credit)', value: balanceData.secondaryGlOverdue, isProfit: true },
      { label: 'CD Overdue (Credit)', value: balanceData.secondaryCdOverdue, isProfit: true },
      { label: 'TBD Overdue (Credit)', value: balanceData.secondaryTbdOverdue, isProfit: true },
      { label: 'Partner Transactions (Credit)', value: balanceData.secondaryPartnerCredits, isProfit: false },
      { label: 'Excel Transaction (Credit)', value: balanceData.secondaryExcelCredits, isProfit: false },
      { label: 'Document charges (Credit)', value: balanceData.secondaryDocumentChargesCredit || 0, isProfit: true },
      { label: 'RD Credit', value: balanceData.secondaryRdCredit, isProfit: false },
      { label: 'Pigmy Credit', value: balanceData.secondaryPigmyCredit, isProfit: false },
    ];

    return { debits, credits };
  }, [balanceData]);

  const getTotalCredit = () => {
    if (!balanceData) return 0;
    return balanceData.secondaryTotalCredit || 0;
  };

  const getTotalDebit = () => {
    if (!balanceData) return 0;
    return balanceData.secondaryTotalDebit || 0;
  };

  const getOverallBalance = () => {
    if (!balanceData) return 0;
    return balanceData.secondaryOverallBalance || 0;
  };

  const handlePrint = () => {
    if (!printRef.current) {
      return;
    }

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=800,width=1200');
    
    // Format dates for display
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const dateRangeText = activeDateFrom && activeDateTo 
      ? `${formatDateForDisplay(activeDateFrom)} to ${formatDateForDisplay(activeDateTo)}`
      : 'All Time (No Date Filter)';

    const printDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Secondary Balance Enquiry</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            @page {
              size: A4;
              margin: 1.5cm 1.2cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 0;
              background: white;
              font-size: 11px;
            }
            .print-header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #343a40;
            }
            .company-name {
              font-size: 18px;
              font-weight: bold;
              color: #d32f2f;
              margin-bottom: 5px;
            }
            .location {
              font-size: 12px;
              color: #555;
              margin-bottom: 8px;
            }
            .report-title {
              font-size: 14px;
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 8px;
            }
            .date-info {
              display: flex;
              justify-content: space-between;
              font-size: 10px;
              margin-top: 8px;
              padding: 0 5px;
            }
            .date-range {
              font-weight: 500;
            }
            .print-date {
              font-weight: 500;
            }
            .secondary-balance-table {
              width: 100%;
              border-collapse: collapse;
              background: white;
              margin: 0;
              page-break-inside: avoid;
            }
            .secondary-balance-table th {
              background-color: #343a40;
              color: white;
              padding: 8px 10px;
              text-align: left;
              font-weight: 600;
              font-size: 11px;
            }
            .secondary-balance-table th.right {
              text-align: right;
            }
            .secondary-balance-table td {
              padding: 6px 10px;
              border-bottom: 1px solid #e0e0e0;
              font-size: 10px;
            }
            .secondary-balance-table td.right {
              text-align: right;
            }
            .debit-row {
              background-color: #fff;
            }
            .debit-amount {
              color: #dc3545 !important;
              font-weight: 500;
            }
            .credit-row {
              background-color: #fff;
            }
            .credit-amount {
              color: #28a745 !important;
              font-weight: 500;
            }
            .profit-label {
              display: inline-block;
              background-color: #ffc107;
              color: #000;
              padding: 1px 5px;
              border-radius: 3px;
              font-size: 8px;
              font-weight: 700;
              margin-right: 5px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            .total-separator {
              border-top: 2px solid #343a40;
              background-color: #f8f9fa;
            }
            .total-separator td {
              padding: 4px;
              border: none;
            }
            .total-credit-row {
              background-color: #d4edda !important;
              border-top: 2px solid #28a745;
            }
            .total-credit-row td {
              border-top: 2px solid #28a745;
              font-size: 11px;
              padding: 8px 10px;
            }
            .total-credit-amount {
              color: #155724 !important;
              font-size: 12px;
            }
            .total-debit-row {
              background-color: #f8d7da !important;
              border-top: 2px solid #dc3545;
            }
            .total-debit-row td {
              border-top: 2px solid #dc3545;
              font-size: 11px;
              padding: 8px 10px;
            }
            .total-debit-amount {
              color: #721c24 !important;
              font-size: 12px;
            }
            .overall-balance-row {
              border-top: 2px solid #343a40;
              font-size: 12px;
            }
            .overall-balance-row td {
              padding: 10px;
              font-size: 12px;
            }
            .overall-balance-amount {
              font-size: 13px;
              font-weight: 700;
            }
            .overall-balance-amount.positive {
              color: #28a745 !important;
            }
            .overall-balance-amount.negative {
              color: #dc3545 !important;
            }
            @media print {
              body {
                padding: 0;
                margin: 0;
              }
              .print-header {
                page-break-after: avoid;
              }
              .secondary-balance-table {
                page-break-inside: avoid;
              }
              tr {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="company-name">SHREE DHANESHWARI FINANCE AND LEASING</div>
            <div class="location">Kasgi , Osmanabad</div>
            <div class="report-title">Reference Numbers (System Credits / Debits)</div>
            <div class="date-info">
              <span class="date-range">Date Range: ${dateRangeText}</span>
              <span class="print-date">Print Date: ${printDate}</span>
            </div>
          </div>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="balance-enquiry-container">
      <h2>{title}</h2>

      {!hideFilters && (
      <div className="filter-section">
        <h3>Date Duration</h3>
        <div className="date-filters">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className="apply-button" onClick={handleApplyFilter}>
              Apply Filter
            </button>
            <button className="clear-button" onClick={handleClearFilter}>
              Clear Filter
            </button>
          </div>
        </div>
        <p className="filter-note">
          {dateFrom && dateTo
            ? `Showing data between ${dateFrom} and ${dateTo}`
            : 'Showing cumulative data (no date filter applied).'}
        </p>
      </div>
      )}

      {balanceData && activeDateFrom && activeDateTo ? (
        <div className="secondary-daily-balance-summary">
          <div className="secondary-daily-balance-card">
            <div className="secondary-daily-balance-label">Opening Balance</div>
            <div className={`secondary-daily-balance-value ${(balanceData.secondaryOpeningBalance || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(balanceData.secondaryOpeningBalance)}
            </div>
          </div>
          <div className="secondary-daily-balance-card">
            <div className="secondary-daily-balance-label">Today&apos;s Net</div>
            <div className={`secondary-daily-balance-value ${((balanceData.secondaryDayNetBalance ?? balanceData.secondaryOverallBalance) ?? 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(balanceData.secondaryDayNetBalance ?? balanceData.secondaryOverallBalance ?? 0)}
            </div>
          </div>
          <div className="secondary-daily-balance-card highlight">
            <div className="secondary-daily-balance-label">Closing Balance</div>
            <div className={`secondary-daily-balance-value ${(balanceData.secondaryClosingBalance || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(balanceData.secondaryClosingBalance)}
            </div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="loading-message">Loading balance data…</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : !balanceData ? (
        <div className="no-data-message">No balance data available.</div>
      ) : (
        <div className="balance-table-section">
          <div className="print-button-container">
            <button className="print-button" onClick={handlePrint}>
              Print
            </button>
          </div>
          <div ref={printRef}>
            <table className="balance-table secondary-balance-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {balanceData.secondaryOpeningBalance != null ? (
                <tr className="total-row overall-balance-row">
                  <td><strong>Opening Balance</strong></td>
                  <td className="right">
                    <strong className={`overall-balance-amount ${balanceData.secondaryOpeningBalance >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(balanceData.secondaryOpeningBalance)}
                    </strong>
                  </td>
                </tr>
              ) : null}
              {/* Debits Section */}
              {secondaryRows.debits.map((row, index) => (
                <tr key={`debit-${index}`} className="debit-row">
                  <td>{row.label}</td>
                  <td className="right debit-amount">{formatCurrency(row.value)}</td>
                </tr>
              ))}
              
              {/* Credits Section */}
              {secondaryRows.credits.map((row, index) => (
                <tr key={`credit-${index}`} className="credit-row">
                  <td>
                    {row.isProfit && <span className="profit-label">PROFIT</span>}
                    {row.label}
                  </td>
                  <td className="right credit-amount">{formatCurrency(row.value)}</td>
                </tr>
              ))}
              
              {/* Totals Section */}
              <tr className="total-separator">
                <td colSpan="2"></td>
              </tr>
              <tr className="total-row total-credit-row">
                <td><strong>Total Credit</strong></td>
                <td className="right"><strong className="total-credit-amount">{formatCurrency(getTotalCredit())}</strong></td>
              </tr>
              <tr className="total-row total-debit-row">
                <td><strong>Total Debit</strong></td>
                <td className="right"><strong className="total-debit-amount">{formatCurrency(getTotalDebit())}</strong></td>
              </tr>
              <tr className="total-row overall-balance-row">
                <td><strong>Period Net Balance</strong></td>
                <td className="right">
                  <strong className={`overall-balance-amount ${getOverallBalance() >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(getOverallBalance())}
                  </strong>
                </td>
              </tr>
              {balanceData.secondaryClosingBalance != null ? (
                <tr className="total-row overall-balance-row">
                  <td><strong>Closing Balance</strong></td>
                  <td className="right">
                    <strong className={`overall-balance-amount ${balanceData.secondaryClosingBalance >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(balanceData.secondaryClosingBalance)}
                    </strong>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecondaryBalanceEnquiry;
